import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import api from '../api/axios';
import { formatProductData, generateSlug } from '../helper/formatProductData';
import { useGlobal } from '../context/GlobalContext';
import { ProductCardSkeleton } from './SkeletonLoader';

// ---- helpers -------------------------------------------------------------

// Pulls a plain number out of a price string like "$12.99" or "₹499"
const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (!price) return 0;
    const num = parseFloat(String(price).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? 0 : num;
};

const sortOptions = [
    { value: 'default', label: 'Default', icon: 'ph ph-arrows-down-up' },
    { value: 'price-asc', label: 'Price: Low to High', icon: 'ph ph-trend-up' },
    { value: 'price-desc', label: 'Price: High to Low', icon: 'ph ph-trend-down' },
    { value: 'name-asc', label: 'Name: A to Z', icon: 'ph ph-sort-ascending' },
    { value: 'name-desc', label: 'Name: Z to A', icon: 'ph ph-sort-descending' },
    { value: 'newest', label: 'Newest', icon: 'ph ph-sparkle' },
];

const ShopSection = () => {
    const { categories: globalCategories, addToCart, cartItems, wishlistItems, addToWishlist, removeFromWishlist } = useGlobal();
    const [productsData, setProductsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [grid, setGrid] = useState(false);
    const [active, setActive] = useState(false);
    const [activeCategory, setActiveCategory] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("default");
    const [isSortOpen, setIsSortOpen] = useState(false);
    const sortRef = useRef(null);
    const [activeSubcategory, setActiveSubcategory] = useState(null); // null = no subcategory filter

    const [openSections, setOpenSections] = useState({
        category: true,
        type: true,
        price: true,
        tags: true,
    });
    const [selectedTags, setSelectedTags] = useState([]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await api.get('/api/products');
                const formatted = response.data.map(formatProductData);
                setProductsData(formatted);
                setLoading(false);
            } catch (err) {
                console.error("Failed to fetch products", err);
                setError("Could not load products. Please try again later.");
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sortRef.current && !sortRef.current.contains(event.target)) {
                setIsSortOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // We no longer have local duplicate separation, everything is fetched
    const uniqueProducts = productsData;

    // Extract unique tags natively from products
    const allTags = [...new Set(uniqueProducts.flatMap(p => p.tags || []))].filter(Boolean);

    // Parse URL parameters for initial filters
    const location = useLocation();
    const { slug } = useParams();
    const [exactProductSlug, setExactProductSlug] = useState(null);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const searchParam = queryParams.get('search');

        if (slug) {
            setSearchQuery("");
            setSelectedTags([]);

            const matchedCat = globalCategories.find(c => generateSlug(c.label) === slug.toLowerCase());
            if (matchedCat) {
                setActiveCategory(String(matchedCat.id));
                setActiveSubcategory(null);
                setExactProductSlug(null);
            } else {
                let matchedSubCat = null;
                for (const cat of globalCategories) {
                    if (cat.subcategories) {
                        const f = cat.subcategories.find(s => generateSlug(s.label) === slug.toLowerCase());
                        if (f) { matchedSubCat = f; break; }
                    }
                }
                if (matchedSubCat) {
                    setActiveCategory("All");
                    setActiveSubcategory(String(matchedSubCat.id));
                    setExactProductSlug(null);
                } else {
                    setExactProductSlug(slug);
                    setActiveCategory("All");
                    setActiveSubcategory(null);
                }
            }
        } else if (searchParam) {
            setExactProductSlug(null);
            const decodedSearch = decodeURIComponent(searchParam);
            // If search exactly matches a tag, set it as a selected tag instead of search query
            if (allTags.some(t => t.toLowerCase() === decodedSearch.toLowerCase())) {
                const matchedTag = allTags.find(t => t.toLowerCase() === decodedSearch.toLowerCase());
                setSelectedTags([matchedTag]);
                setSearchQuery("");
                setActiveCategory("All");
            } else {
                setSearchQuery(decodedSearch);
                setSelectedTags([]);
                setActiveCategory("All");
            }
        } else {
            setExactProductSlug(null);
            setSearchQuery("");
            setSelectedTags([]);
            const categoryParam = queryParams.get('category');
            const subcategoryParam = queryParams.get('subcategory');
            if (subcategoryParam) {
                setActiveSubcategory(decodeURIComponent(subcategoryParam));
                setActiveCategory("All"); // clear main category filter
            } else if (categoryParam) {
                setActiveCategory(decodeURIComponent(categoryParam));
                setActiveSubcategory(null);
            } else {
                setActiveCategory("All");
                setActiveSubcategory(null);
            }
        }
    }, [location.search, allTags.join(','), slug, globalCategories]);

    const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

    // Get categories directly from GlobalContext to ensure empty ones appear
    const dynamicCategories = [{ id: "All", label: "All" }, ...globalCategories.map(c => ({ id: String(c.id), label: c.label || c.name }))];
    // Fallback merge any categories from products that might not be in context
    const fallbackCategories = [];
    uniqueProducts.forEach(p => {
        if (p.categoryId && p.category && !dynamicCategories.some(c => c.id === String(p.categoryId))) {
            fallbackCategories.push({ id: String(p.categoryId), label: p.category });
        }
    });
    const categories = [...dynamicCategories, ...fallbackCategories];

    // Currency symbol, sniffed from the data so the slider matches whatever currency is in use
    const currencySymbol = (String(uniqueProducts[0]?.currentPrice || '$').match(/^[^\d]*/)?.[0].trim()) || '$';

    // Price bounds for the slider, derived from the actual product data
    const allPriceValues = uniqueProducts.map(p => parsePrice(p.currentPrice));
    const dataMin = Math.floor(Math.min(...allPriceValues, 0));
    const dataMax = Math.ceil(Math.max(...allPriceValues, 1));

    const [priceRange, setPriceRange] = useState(null); // null = not yet initialized

    // Sync price range when products load for the first time
    useEffect(() => {
        if (uniqueProducts.length > 0 && priceRange === null) {
            setPriceRange([dataMin, dataMax]);
        }
    }, [uniqueProducts.length]); // eslint-disable-line

    // Get count
    const getCategoryCount = (catId) => {
        if (catId === "All") return uniqueProducts.length;
        return uniqueProducts.filter(p => String(p.categoryId) === String(catId)).length;
    };

    const sidebarController = () => {
        setActive(!active);
    };

    const handleMinPriceChange = (e) => {
        if (!priceRange) return;
        const value = Math.min(Number(e.target.value), priceRange[1] - 1);
        setPriceRange([value, priceRange[1]]);
    };

    const handleMaxPriceChange = (e) => {
        if (!priceRange) return;
        const value = Math.max(Number(e.target.value), priceRange[0] + 1);
        setPriceRange([priceRange[0], value]);
    };

    const toggleTag = (tag) => {
        setSelectedTags((prev) =>
            prev.includes(tag) ? prev.filter((v) => v !== tag) : [...prev, tag]
        );
    };

    const resetFilters = () => {
        setActiveCategory("All");
        setActiveSubcategory(null);
        setExactProductSlug(null);
        setSearchQuery("");
        setSelectedTags([]);
        setPriceRange([dataMin, dataMax]);
        setSortBy("default");
    };

    // ---- filtering pipeline ----
    let filteredProducts = uniqueProducts;
    if (activeSubcategory) {
        filteredProducts = uniqueProducts.filter(p =>
            p.subCategoryId && String(p.subCategoryId) === String(activeSubcategory)
        );
    } else if (activeCategory !== "All") {
        filteredProducts = uniqueProducts.filter(p => String(p.categoryId) === String(activeCategory));
    } else if (exactProductSlug) {
        filteredProducts = uniqueProducts.filter(p => p.slug === exactProductSlug || generateSlug(p.name) === exactProductSlug);
        // fallback in case products are not fully loaded/matched yet
        if (filteredProducts.length === 0) filteredProducts = uniqueProducts;
    }

    // Filter by tags
    if (selectedTags.length > 0) {
        filteredProducts = filteredProducts.filter((p) =>
            p.tags && selectedTags.some(tag => p.tags.includes(tag))
        );
    }

    // Filter by search query if it exists
    if (searchQuery) {
        filteredProducts = filteredProducts.filter(p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.category && p.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (p.tags && p.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())))
        );
    }

    if (priceRange) {
        filteredProducts = filteredProducts.filter((p) => {
            const price = parsePrice(p.currentPrice);
            return price >= priceRange[0] && price <= priceRange[1];
        });
    }

    // ---- sorting ----
    filteredProducts = [...filteredProducts].sort((a, b) => {
        switch (sortBy) {
            case 'price-asc':
                return parsePrice(a.currentPrice) - parsePrice(b.currentPrice);
            case 'price-desc':
                return parsePrice(b.currentPrice) - parsePrice(a.currentPrice);
            case 'name-asc':
                return a.name.localeCompare(b.name);
            case 'name-desc':
                return b.name.localeCompare(a.name);
            case 'newest':
                if (a.dateAdded && b.dateAdded) return new Date(b.dateAdded) - new Date(a.dateAdded);
                return (b.id > a.id ? 1 : -1);
            default:
                return 0;
        }
    });

    const minPercent = priceRange ? ((priceRange[0] - dataMin) / (dataMax - dataMin || 1)) * 100 : 0;
    const maxPercent = priceRange ? ((priceRange[1] - dataMin) / (dataMax - dataMin || 1)) * 100 : 100;

    return (
        <section className="shop py-80">
            {/* Scoped styles matching the reference layout: sidebar sections, price slider, sort control, equal-size cards */}
            <style>{`
                .shop-product-grid {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 16px;
                    width: 100%;
                }
                @media (min-width: 1400px) {
                    .shop-product-grid {
                        grid-template-columns: repeat(4, minmax(0, 1fr));
                        gap: 18px;
                    }
                }
                @media (max-width: 1199px) {
                    .shop-product-grid {
                        grid-template-columns: repeat(3, minmax(0, 1fr));
                        gap: 14px;
                    }
                }
                @media (max-width: 767px) {
                    .shop-product-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 12px;
                    }
                }
                @media (max-width: 420px) {
                    .shop-product-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 8px;
                    }
                }
                .shop-wishlist-btn {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 5;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .shop-wishlist-btn:hover {
                    transform: scale(1.1);
                }
                @media (max-width: 576px) {
                    .shop-wishlist-btn {
                        top: 8px;
                        left: 8px;
                        width: 26px;
                        height: 26px;
                    }
                    .shop-wishlist-btn i {
                        font-size: 13px;
                    }
                }
                .shop-product-card {
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: stretch !important;
                    background: #fff;
                    width: 100% !important;
                    min-width: 0;
                    height: 100%;
                    box-sizing: border-box;
                    border-radius: 14px;
                    padding: 10px;
                    transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
                }
                .shop-product-card__thumb {
                    flex: 0 0 140px;
                    width: 100% !important;
                    height: 140px;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    padding: 6px;
                }
                .shop-product-card__thumb img {
                    width: auto;
                    height: 100%;
                    max-width: 100%;
                    max-height: 130px;
                    object-fit: contain;
                    margin: 0 auto;
                }
                .shop-product-card__action {
                    width: 100%;
                    margin: 8px 0;
                }
                .shop-product-card .product-card__cart {
                    width: 100% !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    text-align: center !important;
                    padding: 8px 14px !important;
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    border-radius: 999px !important;
                    box-sizing: border-box !important;
                    white-space: nowrap !important;
                }
                .shop-product-card__content {
                    flex: 1 1 auto !important;
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: stretch !important;
                    width: 100% !important;
                    min-width: 0;
                }
                .shop-product-card__title {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    line-height: 1.4;
                    min-height: 2.8em;
                    margin-bottom: 0;
                    width: 100% !important;
                }
                @media (max-width: 576px) {
                    .shop-product-card {
                        padding: 8px !important;
                    }
                    .shop-product-card__thumb {
                        flex: 0 0 110px !important;
                        height: 110px !important;
                        padding: 4px !important;
                    }
                    .shop-product-card__thumb img {
                        max-height: 100px !important;
                    }
                    .shop-product-card .product-card__cart {
                        padding: 6px 10px !important;
                        font-size: 11px !important;
                        gap: 4px !important;
                    }
                    .shop-product-card .product-card__cart i {
                        font-size: 12px !important;
                    }
                }

                /* ---- Sidebar accordion sections ---- */
                .shop-sidebar__box {
                    background: #fff;
                }
                .shop-sidebar__accordion-header {
                    background: none;
                    border: none;
                    padding: 0;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                    text-align: left;
                }
                .shop-sidebar__accordion-header:focus-visible {
                    outline: 2px solid var(--main-600, #2b8a3e);
                    outline-offset: 4px;
                }
                .shop-sidebar__accordion-icon {
                    transition: transform 0.2s ease;
                    color: var(--main-600, #2b8a3e);
                }
                .shop-sidebar__checkbox-row {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 8px;
                    cursor: pointer;
                    width: 100%;
                    line-height: 1.4;
                }
                .shop-sidebar__checkbox-row input[type="checkbox"] {
                    accent-color: var(--main-600, #2b8a3e);
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                }

                /* ---- Dual price range slider ---- */
                .price-range-slider {
                    position: relative;
                    height: 20px;
                    margin-top: 8px;
                }
                .price-range-slider__track {
                    position: absolute;
                    top: 50%;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: #e6e6e6;
                    border-radius: 999px;
                    transform: translateY(-50%);
                }
                .price-range-slider__range {
                    position: absolute;
                    top: 50%;
                    height: 4px;
                    background: var(--main-600, #2b8a3e);
                    border-radius: 999px;
                    transform: translateY(-50%);
                }
                .price-range-slider__input {
                    position: absolute;
                    top: 50%;
                    left: 0;
                    width: 100%;
                    transform: translateY(-50%);
                    -webkit-appearance: none;
                    appearance: none;
                    background: transparent;
                    pointer-events: none;
                    margin: 0;
                }
                .price-range-slider__input::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    pointer-events: auto;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background: var(--main-600, #2b8a3e);
                    border: 2px solid #fff;
                    box-shadow: 0 0 0 1px var(--main-600, #2b8a3e);
                    cursor: pointer;
                }
                .price-range-slider__input::-moz-range-thumb {
                    pointer-events: auto;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background: var(--main-600, #2b8a3e);
                    border: 2px solid #fff;
                    box-shadow: 0 0 0 1px var(--main-600, #2b8a3e);
                    cursor: pointer;
                }
                .price-range-slider__input::-webkit-slider-runnable-track {
                    -webkit-appearance: none;
                    background: transparent;
                }
                .price-range-value {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1a1a1a;
                    border: 1px solid #e6e6e6;
                    border-radius: 6px;
                    padding: 6px 14px;
                    flex: 1;
                }
                .price-range-value:first-child { text-align: left; }
                .price-range-value:last-child { text-align: right; }

                /* ---- Modern Sort by Dropdown ---- */
                .sort-by-wrapper {
                    position: relative;
                    user-select: none;
                }
                .sort-by-trigger {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: #ffffff;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 8px 14px;
                    cursor: pointer;
                    outline: none;
                    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
                }
                .sort-by-trigger:hover,
                .sort-by-trigger.active {
                    border-color: #24593E;
                    box-shadow: 0 0 0 3px rgba(36, 89, 62, 0.12);
                    background: #ffffff;
                }
                .sort-by-trigger .sort-caret {
                    font-size: 11px;
                    color: #64748b;
                    transition: transform 0.2s ease;
                }
                .sort-by-trigger .sort-caret.rotate-180 {
                    transform: rotate(180deg);
                }
                .sort-by-dropdown-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    min-width: 215px;
                    background: #ffffff;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 12px 28px -4px rgba(15, 23, 42, 0.12), 0 4px 10px -2px rgba(15, 23, 42, 0.06);
                    padding: 6px;
                    z-index: 1050;
                    animation: sortMenuFadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes sortMenuFadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(-6px) scale(0.98);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .sort-by-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    padding: 8px 12px;
                    border-radius: 8px;
                    font-size: 13.5px;
                    font-weight: 500;
                    color: #334155;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .sort-by-item:hover {
                    background-color: #f8fafc;
                    color: #0f172a;
                }
                .sort-by-item.selected {
                    background-color: #f0fdf4;
                    color: #166534;
                    font-weight: 600;
                }
                .sort-by-item.selected i.ph-check {
                    color: #16a34a;
                }
                @media (max-width: 575px) {
                    .sort-by-dropdown-menu {
                        left: auto;
                        right: 0;
                        min-width: 200px;
                    }
                }

                /* ---- Scrollable sidebar drawer on small screens ---- */
                @media (max-width: 991.98px) {
                    .shop-sidebar.active {
                        overflow-y: auto;
                        max-height: 100vh;
                    }
                }
            `}</style>

            <div className={`side-overlay ${active && "show"}`} onClick={sidebarController}></div>
            <div className="container container-lg"  >
                <div className="row">
                    {/* Sidebar Start */}
                    <div className="col-lg-3">
                        <div className={`shop-sidebar ${active && "active"}`}>
                            <button onClick={sidebarController}
                                type="button"
                                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600"
                            >
                                <i className="ph ph-x" />
                            </button>

                            {/* Product Category */}
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <div className="border-bottom border-gray-100 pb-24 mb-24">
                                    <button
                                        type="button"
                                        className="shop-sidebar__accordion-header"
                                        onClick={() => toggleSection('category')}
                                    >
                                        <h6 className="text-xl mb-0">Product Category</h6>
                                        <i className={`ph ph-caret-${openSections.category ? 'up' : 'down'} shop-sidebar__accordion-icon text-lg`} />
                                    </button>
                                </div>
                                {openSections.category && (
                                    <ul className="max-h-540 overflow-y-auto scroll-sm">
                                        {categories.map((cat, idx) => (
                                            <li className={idx === categories.length - 1 ? "" : "mb-24"} key={idx}>
                                                <button
                                                    onClick={() => {
                                                        setActiveCategory(String(cat.id));
                                                        setActiveSubcategory(null);
                                                        setExactProductSlug(null);
                                                        setSearchQuery("");
                                                        setSelectedTags([]);
                                                    }}
                                                    className={`text-gray-900 hover-text-main-600 ${activeCategory === String(cat.id) ? "fw-bold text-main-600" : ""}`}
                                                    style={{ background: 'none', border: 'none', padding: 0, outline: 'none', textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                                                >
                                                    <span style={{ flex: 1, wordBreak: 'break-word', paddingRight: '8px' }}>{cat.label}</span>
                                                    <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>({getCategoryCount(cat.id)})</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Product Type / Tags */}
                            {/* {allTags.length > 0 && (
                                <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                    <div className="border-bottom border-gray-100 pb-24 mb-24">
                                        <button
                                            type="button"
                                            className="shop-sidebar__accordion-header"
                                            onClick={() => toggleSection('type')}
                                        >
                                            <h6 className="text-xl mb-0">Product Type</h6>
                                            <i className={`ph ph-caret-${openSections.type ? 'up' : 'down'} shop-sidebar__accordion-icon text-lg`} />
                                        </button>
                                    </div>
                                    {openSections.type && (
                                        <ul className="max-h-280 overflow-y-auto scroll-sm">
                                            {allTags.map((tag, idx) => {
                                                const tagCount = uniqueProducts.filter(p => p.tags && p.tags.includes(tag)).length;
                                                return (
                                                    <li className={idx === allTags.length - 1 ? "" : "mb-16"} key={idx}>
                                                        <label className="shop-sidebar__checkbox-row text-gray-900">
                                                            <span className="flex-align gap-8">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedTags.includes(tag)}
                                                                    onChange={() => toggleTag(tag)}
                                                                />
                                                                <span style={{ flex: 1, wordBreak: 'break-word', paddingRight: '8px' }}>{tag}</span>
                                                            </span>
                                                            <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>({tagCount})</span>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            )} */}

                            {/* Price Range */}
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <div className="border-bottom border-gray-100 pb-24 mb-24">
                                    <button
                                        type="button"
                                        className="shop-sidebar__accordion-header"
                                        onClick={() => toggleSection('price')}
                                    >
                                        <h6 className="text-xl mb-0">Price Range</h6>
                                        <i className={`ph ph-caret-${openSections.price ? 'up' : 'down'} shop-sidebar__accordion-icon text-lg`} />
                                    </button>
                                </div>
                                {openSections.price && (
                                    <div>
                                        <div className="price-range-slider">
                                            <div className="price-range-slider__track" />
                                            <div
                                                className="price-range-slider__range"
                                                style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
                                            />
                                            <input
                                                type="range"
                                                className="price-range-slider__input"
                                                min={dataMin}
                                                max={dataMax}
                                                value={priceRange ? priceRange[0] : dataMin}
                                                onChange={handleMinPriceChange}
                                                aria-label="Minimum price"
                                            />
                                            <input
                                                type="range"
                                                className="price-range-slider__input"
                                                min={dataMin}
                                                max={dataMax}
                                                value={priceRange ? priceRange[1] : dataMax}
                                                onChange={handleMaxPriceChange}
                                                aria-label="Maximum price"
                                            />
                                        </div>
                                        <div className="flex-align gap-16 mt-24">
                                            <span className="price-range-value">{currencySymbol}{priceRange ? priceRange[0] : dataMin}</span>
                                            <span className="price-range-value">{currencySymbol}{priceRange ? priceRange[1] : dataMax}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={resetFilters}
                                className="btn border border-gray-100 text-gray-900 hover-bg-main-600 hover-text-white hover-border-main-600 w-100 py-12 rounded-8"
                            >
                                Clear All Filters
                            </button>
                        </div>
                    </div>
                    {/* Content Start */}
                    <div className="col-lg-9">
                        {loading ? (
                            <div className="text-center py-80">
                                <div className="spinner-border text-main-600" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-16 text-gray-500">Fetching products...</p>
                            </div>
                        ) : error ? (
                            <div className="text-center py-80">
                                <i className="ph ph-warning-circle text-4xl text-danger-600 mb-16 d-block" />
                                <p className="text-danger-600 mb-16">{error}</p>
                            </div>
                        ) : (
                            <>
                                {/* Top Start */}
                                <div className="flex-between gap-16 flex-wrap mb-40 ">
                                    <span className="text-gray-900">Showing {filteredProducts.length} results</span>
                                    <div className="position-relative flex-align gap-16 flex-wrap">
                                        <div className="sort-by-wrapper" ref={sortRef}>
                                            <button
                                                type="button"
                                                className={`sort-by-trigger ${isSortOpen ? 'active' : ''}`}
                                                onClick={() => setIsSortOpen(!isSortOpen)}
                                                aria-expanded={isSortOpen}
                                                aria-haspopup="listbox"
                                            >
                                                <i className="ph ph-sort-ascending text-lg text-main-600" />
                                                <span className="text-sm fw-semibold text-neutral-800">
                                                    {(sortOptions.find(o => o.value === sortBy) || sortOptions[0]).label}
                                                </span>
                                                <i className={`ph ph-caret-down sort-caret ${isSortOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isSortOpen && (
                                                <div className="sort-by-dropdown-menu" role="listbox">
                                                    {sortOptions.map((option) => {
                                                        const isSelected = sortBy === option.value;
                                                        return (
                                                            <div
                                                                key={option.value}
                                                                role="option"
                                                                aria-selected={isSelected}
                                                                className={`sort-by-item ${isSelected ? 'selected' : ''}`}
                                                                onClick={() => {
                                                                    setSortBy(option.value);
                                                                    setIsSortOpen(false);
                                                                }}
                                                            >
                                                                <div className="flex-align gap-8">
                                                                    <i className={`${option.icon} text-md ${isSelected ? 'text-main-600' : 'text-gray-500'}`} />
                                                                    <span>{option.label}</span>
                                                                </div>
                                                                {isSelected && (
                                                                    <i className="ph-bold ph-check text-sm" />
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <div className="list-grid-btns flex-align gap-16">
                                            <button onClick={() => setGrid(false)}
                                                type="button"
                                                className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${!grid && "border-main-600 text-white bg-main-600"}`}
                                            >
                                                <i className="ph ph-squares-four" />
                                            </button>
                                            <button onClick={() => setGrid(true)}
                                                type="button"
                                                className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${grid && "border-main-600 text-white bg-main-600"}`}
                                            >
                                                <i className="ph-bold ph-list-dashes" />
                                            </button>
                                        </div>
                                        <button onClick={sidebarController}
                                            type="button"
                                            className="w-44 h-44 d-lg-none d-flex flex-center border border-gray-100 rounded-6 text-2xl sidebar-btn"
                                        >
                                            <i className="ph-bold ph-funnel" />
                                        </button>
                                    </div>
                                </div>
                                {/* Top End */}

                                {loading ? (
                                    <div className="shop-product-grid">
                                        {Array.from({ length: 8 }).map((_, idx) => (
                                            <ProductCardSkeleton key={idx} />
                                        ))}
                                    </div>
                                ) : filteredProducts.length === 0 ? (
                                    <div className="text-center py-80">
                                        <i className="ph ph-magnifying-glass text-4xl text-gray-400 mb-16 d-block" />
                                        <p className="text-gray-500 mb-16">No products match the selected filters.</p>
                                        <button
                                            type="button"
                                            onClick={resetFilters}
                                            className="btn bg-main-600 text-white py-12 px-32 rounded-8"
                                        >
                                            Clear All Filters
                                        </button>
                                    </div>
                                ) : !grid ? (
                                    /* GRID VIEW — 4 equal square cards per row on desktop */
                                    <div className="shop-product-grid">
                                        {filteredProducts.map((product, index) => {
                                            const isInCart = cartItems?.some(item => String(item.id) === String(product.id));
                                            const isWishlisted = wishlistItems?.some(item => String(item.id) === String(product.id));
                                            return (
                                            <div
                                                key={product.id}
                                                className="shop-product-card product-card hover-lift p-12 border border-gray-100 hover-border-main-600 rounded-16 position-relative"
                                            >
                                                {/* Wishlist Heart Button */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (isWishlisted) {
                                                            removeFromWishlist(product.id);
                                                        } else {
                                                            addToWishlist(product);
                                                        }
                                                    }}
                                                    className={`shop-wishlist-btn border ${
                                                        isWishlisted
                                                            ? 'bg-danger-50 border-danger-200 text-danger-600'
                                                            : 'bg-white border-gray-100 text-gray-400 hover-text-main-600 hover-border-main-600 shadow-xs'
                                                    }`}
                                                    title={isWishlisted ? "In Wishlist (Click to remove)" : "Add to Wishlist"}
                                                    aria-label="Wishlist"
                                                >
                                                    <i className={`${isWishlisted ? 'ph-fill ph-heart text-danger-600' : 'ph ph-heart'} text-base`} />
                                                </button>

                                                {/* 1. Photo */}
                                                <Link
                                                    to={`/product-details/${product.slug || product.id}`}
                                                    className="shop-product-card__thumb flex-center"
                                                >
                                                    <img src={product.image} alt={product.name} />
                                                </Link>

                                                {/* 2. Middle: Add to Cart / View Cart button */}
                                                <div className="shop-product-card__action">
                                                    {isInCart ? (
                                                        <Link
                                                            to="/cart"
                                                            className="product-card__cart btn bg-main-600 text-white hover-bg-main-700 py-6 px-12 text-xs rounded-pill flex-center gap-6 w-100"
                                                        >
                                                            View Cart <i className="ph ph-shopping-cart" />
                                                        </Link>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.preventDefault(); addToCart(product, 1, product.defaultVariant || (product.rawVariants && product.rawVariants.length > 0 ? product.rawVariants[0] : null)); }}
                                                            className="product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-6 px-12 text-xs rounded-pill flex-center gap-6 w-100"
                                                        >
                                                            Add to Cart <i className="ph ph-shopping-cart" />
                                                        </button>
                                                    )}
                                                </div>

                                                {/* 3. Text details (Price & Name) */}
                                                <div className="shop-product-card__content mt-4">
                                                    <div className="product-card__price mb-4 d-flex align-items-center justify-content-start flex-wrap gap-6">
                                                        <div className="d-flex align-items-center gap-6 flex-wrap">
                                                            <span className="text-heading text-sm fw-semibold">
                                                                {product.currentPrice}{' '}
                                                                <span className="text-gray-500 fw-normal text-xs">
                                                                    {product.quantityText}
                                                                </span>
                                                            </span>
                                                            {product.originalPrice && (
                                                                <span className="text-gray-400 text-xs fw-semibold text-decoration-line-through">
                                                                    {product.originalPrice}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {product.rawVariants && product.rawVariants.length > 1 && (
                                                            <span
                                                                className="badge rounded-4 fw-medium"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    color: '#4b5563',
                                                                    background: '#f3f4f6',
                                                                    border: '1px solid #e5e7eb',
                                                                    padding: '1px 5px',
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 0
                                                                }}
                                                                title={`${product.rawVariants.length} options available`}
                                                            >
                                                                +{product.rawVariants.length} options
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h6 className="shop-product-card__title title text-sm fw-semibold mb-0">
                                                        <Link to={`/product-details/${product.slug || product.id}`} className="link text-line-2">
                                                            {product.name}
                                                        </Link>
                                                    </h6>
                                                </div>
                                            </div>
                                        );
                                        })}
                                    </div>
                                ) : (
                                    /* LIST VIEW — unchanged existing layout */
                                    <div className="list-grid-wrapper list-view">
                                        {filteredProducts.map((product) => {
                                             const isInCart = cartItems?.some(item => String(item.id) === String(product.id));
                                             const isWishlisted = wishlistItems?.some(item => String(item.id) === String(product.id));
                                            return (
                                            <div key={product.id} className="product-card p-12 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2">
                                                {/* Wishlist Heart Button */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (isWishlisted) {
                                                            removeFromWishlist(product.id);
                                                        } else {
                                                            addToWishlist(product);
                                                        }
                                                    }}
                                                    className={`shop-wishlist-btn border ${
                                                        isWishlisted
                                                            ? 'bg-danger-50 border-danger-200 text-danger-600'
                                                            : 'bg-white border-gray-100 text-gray-400 hover-text-main-600 hover-border-main-600 shadow-xs'
                                                    }`}
                                                    title={isWishlisted ? "In Wishlist (Click to remove)" : "Add to Wishlist"}
                                                    aria-label="Wishlist"
                                                >
                                                    <i className={`${isWishlisted ? 'ph-fill ph-heart text-danger-600' : 'ph ph-heart'} text-base`} />
                                                </button>

                                                <Link
                                                    to={`/product-details/${product.slug || product.id}`}
                                                    className="product-card__thumb flex-center"
                                                >
                                                    <img
                                                        src={product.image}
                                                        alt={product.name}
                                                        style={{ objectFit: 'contain', maxHeight: '120px' }}
                                                    />
                                                </Link>

                                                <div className="product-card__content mt-8">
                                                    <div className="product-card__price mb-4 d-flex align-items-center gap-8 flex-wrap">
                                                        <span className="text-heading text-sm fw-semibold">
                                                            {product.currentPrice}{' '}
                                                            <span className="text-gray-500 fw-normal text-xs">
                                                                {product.quantityText}
                                                            </span>
                                                        </span>
                                                        {product.originalPrice && (
                                                            <span className="text-gray-400 text-sm fw-semibold text-decoration-line-through">
                                                                {product.originalPrice}
                                                            </span>
                                                        )}
                                                        {product.rawVariants && product.rawVariants.length > 1 && (
                                                            <span
                                                                className="badge rounded-4 fw-medium ms-auto"
                                                                style={{
                                                                    fontSize: '11px',
                                                                    color: '#4b5563',
                                                                    background: '#f3f4f6',
                                                                    border: '1px solid #e5e7eb',
                                                                    padding: '2px 6px',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                                title={`${product.rawVariants.length} options available`}
                                                            >
                                                                +{product.rawVariants.length} options
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h6 className="title text-sm fw-semibold mb-0">
                                                        <Link to={`/product-details/${product.slug || product.id}`} className="link text-line-2">
                                                            {product.name}
                                                        </Link>
                                                    </h6>

                                                    {isInCart ? (
                                                        <Link
                                                            to="/cart"
                                                            className="product-card__cart btn bg-main-600 text-white hover-bg-main-700 py-8 px-12 text-sm rounded-8 flex-center gap-8 mt-16 w-100"
                                                        >
                                                            View Cart <i className="ph ph-shopping-cart" />
                                                        </Link>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.preventDefault(); addToCart(product, 1, product.defaultVariant || (product.rawVariants && product.rawVariants.length > 0 ? product.rawVariants[0] : null)); }}
                                                            className="product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-8 px-12 text-sm rounded-8 flex-center gap-8 mt-16 w-100"
                                                        >
                                                            Add to Cart <i className="ph ph-shopping-cart" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {/* Content End */}
                </div>
            </div>
        </section>
    );
};

export default ShopSection;